// src/components/forms/PatientForm.jsx
import React, { useState, useEffect } from 'react';
import BaseForm from './BaseForm';
import FormField from './FormField';
import useForm from '../../hooks/useForm';
import { PATIENT_VALIDATION, GENDER_OPTIONS } from '../../constants/validationRules';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { medicalCenterService } from '../../services/medicalCenterService';

const formatUSPhoneDisplay = (raw) => {
  const d = (raw || '').replace(/\D/g, '').replace(/^1/, '').slice(0, 10);
  const a = d.slice(0, 3), b = d.slice(3, 6), c = d.slice(6, 10);
  if (d.length > 6) return `+1 (${a}) ${b}-${c}`;
  if (d.length > 3) return `+1 (${a}) ${b}`;
  if (d.length > 0) return `+1 (${a}`;
  return '+1 ';
};

const toE164US = (display) => {
  const d = (display || '').replace(/\D/g, '');
  const ten = d.replace(/^1/, '').slice(0, 10);
  return ten.length === 10 ? `+1${ten}` : '';
};

const PatientForm = ({ isOpen, onClose, onSubmit, loading = false, patient = null }) => {
  const { medicalCenterId, selectedMedicalCenter } = useMedicalCenter();
  const [languages, setLanguages] = useState([]);
  const isEditing = Boolean(patient);

  const initialData = {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    birthdate: '',
    gender: '',
    preferred_language: selectedMedicalCenter?.preferred_language || 'en'
  };

  const { formData, errors, handleChange, validate, reset, setFormData } = useForm(initialData, PATIENT_VALIDATION);

  useEffect(() => {
    const loadLanguages = async () => {
      const data = await medicalCenterService.getLanguages();
      setLanguages(data);
    };
    loadLanguages();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (patient) {
      setFormData({
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        email: patient.email || '',
        phone_number: patient.phone_number ? formatUSPhoneDisplay(patient.phone_number) : '',
        birthdate: patient.birthdate ? patient.birthdate.split('T')[0] : '',
        gender: patient.gender || '',
        preferred_language: patient.preferred_language || 'en'
      });
    } else if (selectedMedicalCenter?.preferred_language) {
      setFormData(prev => ({
        ...prev,
        preferred_language: prev.preferred_language || selectedMedicalCenter.preferred_language
      }));
    }
  }, [isOpen, patient, selectedMedicalCenter, setFormData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const payload = {
        ...formData,
        phone_number: toE164US(formData.phone_number)
      };
      onSubmit(payload);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BaseForm
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={isEditing ? 'Edit Patient' : 'Add Patient'}
      loading={loading}
      submitText={isEditing ? 'Update Patient' : 'Add Patient'}
    >
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Patient Details</h3>

        <div className="text-sm text-gray-600">
          <span className="text-gray-500 mr-2">Medical Center:</span>
          <span className="font-medium">
            {selectedMedicalCenter?.name || medicalCenterId || '—'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            error={errors.first_name}
            required
          />
          <FormField
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            error={errors.last_name}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
          />
          <FormField
            label="Phone Number"
            name="phone_number"
            value={formData.phone_number}
            onChange={(e) => {
              const formatted = formatUSPhoneDisplay(e.target.value);
              handleChange({ target: { name: 'phone_number', value: formatted } });
            }}
            error={errors.phone_number}
            placeholder="+1 (___) ___-____"
            required
            inputMode="tel"
            maxLength={18}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Birthdate"
            name="birthdate"
            type="date"
            value={formData.birthdate}
            onChange={handleChange}
            error={errors.birthdate}
            required
          />
          <FormField
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            error={errors.gender}
            required
            options={GENDER_OPTIONS}
          />
        </div>

        <FormField
          label="Preferred Language"
          name="preferred_language"
          value={formData.preferred_language}
          onChange={handleChange}
          error={errors.preferred_language}
          required
          options={languages.map(lang => ({
            value: lang.code,
            label: lang.name
          }))}
        />
        <p className="text-xs text-gray-500 -mt-3">
          Defaults to the medical center's language. Override if the patient prefers a different language.
        </p>
      </div>
    </BaseForm>
  );
};

export default PatientForm;